import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentLocale } from '../i18n';

// Define the GA4 measurement ID
const GA4_MEASUREMENT_ID = 'G-286QCF41P7'; // Replace with your actual GA4 measurement ID when deploying to production

// Define the consent storage key
const CONSENT_STORAGE_KEY = 'analytics_consent';

// Define the context type
interface AnalyticsContextType {
  // Consent state
  hasConsent: boolean;
  setConsent: (consent: boolean) => void;

  // Tracking functions
  trackPageView: (path: string, title?: string) => void;
  trackEvent: (eventName: string, eventParams?: Record<string, any>) => void;

  // Consent management
  showConsentBanner: boolean;
  setShowConsentBanner: (show: boolean) => void;
}

// Create the context with default values
const AnalyticsContext = createContext<AnalyticsContextType>({
  hasConsent: false,
  setConsent: () => {},
  trackPageView: () => {},
  trackEvent: () => {},
  showConsentBanner: false,
  setShowConsentBanner: () => {},
});

// Provider component
export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // State for consent
  const [hasConsent, setHasConsent] = useState<boolean>(false);
  // State for showing the consent banner
  const [showConsentBanner, setShowConsentBanner] = useState<boolean>(true);
  // State to track if GA4 is loaded
  const [isGa4Loaded, setIsGa4Loaded] = useState<boolean>(false);

  // Load consent state from localStorage on mount
  useEffect(() => {
    try {
      const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (storedConsent !== null) {
        const consentValue = storedConsent === 'true';
        setHasConsent(consentValue);
        // If consent was previously given or explicitly denied, don't show the banner
        setShowConsentBanner(storedConsent === null);
      }
    } catch (error) {
      console.error('Error loading analytics consent from localStorage:', error);
    }
  }, []);

  // Function to set consent and save to localStorage
  const setConsent = (consent: boolean) => {
    setHasConsent(consent);
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, consent.toString());
    } catch (error) {
      console.error('Error saving analytics consent to localStorage:', error);
    }
  };

  // Load GA4 script when consent is given
  useEffect(() => {
    if (hasConsent && !isGa4Loaded) {
      // Check if we already have a client ID in localStorage
      const existingClientId = localStorage.getItem('ga_client_id');

      // Create script element
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;

      // Add the script to the document
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      // Initialize GA4
      window.gtag('js', new Date());

      // Get current locale for GA4 language setting
      const currentLocale = getCurrentLocale();

      // Configure with language and existing client ID if available
      const configOptions: any = {
        language: currentLocale,
        send_page_view: false, // We'll handle page views manually
        cookie_expires: 60 * 60 * 24 * 365, // 1 year in seconds
        cookie_domain: 'auto', // Let GA4 determine the correct domain
        cookie_update: true, // Allow updating the cookie to extend expiration
        cookie_flags: 'samesite=lax;secure', // Add security flags
        transport_type: 'beacon' // Use beacon API for more reliable tracking
      };

      // If we have an existing client ID, use it
      if (existingClientId) {
        configOptions.client_id = existingClientId;
      }

      // Add debug mode in development
      if (process.env.NODE_ENV === 'development') {
        configOptions.debug_mode = true;
      }

      window.gtag('config', GA4_MEASUREMENT_ID, configOptions);

      // If we don't have a client ID yet, get and store it
      if (!existingClientId) {
        window.gtag('get', GA4_MEASUREMENT_ID, 'client_id', (clientId: string) => {
          try {
            // Store the client ID in localStorage for the axios interceptor to use
            localStorage.setItem('ga_client_id', clientId);
            console.log('GA4 client ID stored for backend tracking');
          } catch (error) {
            console.error('Error storing GA4 client ID:', error);
          }
        });
      }

      setIsGa4Loaded(true);
    }
  }, [hasConsent, isGa4Loaded]);

  // Track page view
  const trackPageView = (path: string, title?: string) => {
    if (hasConsent && isGa4Loaded && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
        page_location: window.location.href,
        send_to: GA4_MEASUREMENT_ID
      });
    }
  };

  // Track custom event
  const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
    if (hasConsent && isGa4Loaded && window.gtag) {
      // Ensure all events are sent to the correct GA4 property with the same client ID
      const params = {
        ...eventParams,
        send_to: GA4_MEASUREMENT_ID
      };
      window.gtag('event', eventName, params);
    }
  };

  return (
    <AnalyticsContext.Provider
      value={{
        hasConsent,
        setConsent,
        trackPageView,
        trackEvent,
        showConsentBanner,
        setShowConsentBanner,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom hook to use the analytics context
export const useAnalytics = () => useContext(AnalyticsContext);

// Add type definitions for window object
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
